import { DxCombobox } from '@dvag/design-system-react';
import debounce from 'debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchPerson } from '../../HouseholdWidget/service/useSearchPerson';
import { PersonSearchItem } from 'type/person';
import { formatCustomerResultAddress } from 'utils/formatCustomerResultAddress';
import i18next from 'i18next';

const MIN_SEARCH_TERM_LENGTH = 3;
const DEBOUNCE_MS = 1500;

const mapPersonToOption = (person: PersonSearchItem) => ({
  label: `${person.firstName} ${person.lastName}`.trim(),
  subLabel: formatCustomerResultAddress(person.address) || undefined,
  preLabel: i18next.t('sendConsent.consentOrLegalGuardianModal.legalGuardian.customerNo', {
    customerNo: person.id,
  }),
  id: person.id,
});

type Option = ReturnType<typeof mapPersonToOption>;
type OptionGroup = { label?: null; options: Option[] }[];

type CacheState = { term?: string; provided?: OptionGroup };

interface Props {
  onSelect?: (id: string | undefined) => void;
  excludedIds?: string[];
  errormessage?: string;
  onClearErrors?: () => void;
  disabled?: boolean;
  initialOption?: Option | '';
}

export const GuardianOtherPersonCombobox = ({
  onSelect,
  excludedIds = [],
  errormessage,
  onClearErrors,
  initialOption = '',
  disabled = false,
}: Props) => {
  const provideValuesRef = useRef<(groups: OptionGroup) => void>(undefined);
  const cacheRef = useRef<CacheState>({});
  const [currentValue, setCurrentValue] = useState<Option | string>();
  const [searchTerm, setSearchTerm] = useState<string>();

  useEffect(() => {
    if (!currentValue) {
      setCurrentValue(initialOption);
    }
  }, [initialOption, currentValue]);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((term?: string) => {
        cacheRef.current.term = term;
        setSearchTerm(term);
      }, DEBOUNCE_MS),
    [],
  );

  useSearchPerson(searchTerm ?? '', (personList) => {
    const filtered = personList.filter((p) => !excludedIds.includes(p.id));
    const optionGroups: OptionGroup = filtered.length
      ? [{ label: null, options: filtered.map(mapPersonToOption) }]
      : [];
    provideValuesRef.current?.(optionGroups);
    cacheRef.current.provided = optionGroups;
  });

  const handleSearch = useCallback(
    (event: Event) => {
      const { detail } = event as CustomEvent;

      if (!provideValuesRef.current) {
        provideValuesRef.current = detail.provideValues;
      }

      if (!detail.reason) {
        detail.provideValues(cacheRef.current.provided);
        return;
      }

      if (detail.reason === 'clear') {
        onSelect?.(undefined);
        setSearchTerm(undefined);
        setCurrentValue('');
        cacheRef.current = {};
        return;
      }

      if (detail.reason === 'focus') {
        detail.provideValues(cacheRef.current.provided);
        return;
      }

      if (detail.value === cacheRef.current.term) {
        detail.provideValues(cacheRef.current.provided);
      }

      debouncedSetSearchTerm(detail.value);
    },
    [debouncedSetSearchTerm, onSelect],
  );

  useEffect(() => {
    const element = document.querySelector('#guardian-other-person-combobox');
    element?.addEventListener('search', handleSearch);
    return () => element?.removeEventListener('search', handleSearch);
  }, [handleSearch]);

  return (
    <DxCombobox
      id="guardian-other-person-combobox"
      data-testid="guardian-other-person-combobox"
      usagemode="new"
      size="m"
      label={i18next.t('sendConsent.consentOrLegalGuardianModal.legalGuardian.label')}
      required
      presearchlettercount={MIN_SEARCH_TERM_LENGTH}
      value={currentValue}
      errormessage={errormessage}
      disabled={disabled}
      onSelectSearchOption={(e: CustomEvent) => {
        setCurrentValue(e.detail);
        onSelect?.(e.detail?.id);
        onClearErrors?.();
      }}
      onInputChange={(e: CustomEvent) => {
        const val: string | undefined = e.detail?.value;
        if (!val) {
          onSelect?.(undefined);
          setCurrentValue('');
          debouncedSetSearchTerm(undefined);
        }
      }}
    />
  );
};
